import { Chip, Grid, Paper, Stack, Typography } from '@mui/material';
import { CheckCircle, InfoOutlined, NotificationsActive, Refresh, Rule } from '@mui/icons-material';
import { CustomButton, LoadingSkeleton } from '../../../common/components';
import { useNotificationHealth } from '../../../hooks/useNotifications';
import { formatDateTime, statusChip } from '../utils/notificationUtils';
import { Metric } from './MetricsBoard';

const EVENT_TYPE_LABELS = {
  completed_project_contracts_missing_operational_data:
    'Contratos con proyecto finalizado y datos pendientes',
  coupon_due_tomorrow: 'Avisos de vencimiento de cupones',
  coupon_collection_by_due: 'Cobros por vencimiento',
};

const WARNING_LABELS = {
  scheduler_not_running: 'Scheduler detenido',
  failed_jobs_last_24h: 'Ejecuciones fallidas en las últimas 24 hs',
  production_push_disabled_or_non_production: 'Push productivo deshabilitado o entorno no productivo',
  coupon_collection_manual_until_calendar_confirmed: 'Cobros por vencimiento sin calendario confirmado',
};

const JOB_LABELS = {
  notifications_completed_project_missing_operational_data:
    'Contratos con proyecto finalizado y datos pendientes',
  notifications_coupon_due_tomorrow: 'Avisos de vencimiento de cupones',
  notifications_coupon_collection_by_due: 'Cobros por vencimiento',
};

const labelForEventType = (value) => EVENT_TYPE_LABELS[value] || value || '-';
const labelForWarning = (value) => WARNING_LABELS[value] || value || '-';
const labelForJob = (job) => JOB_LABELS[job.id] || job.name || job.id || '-';

const KeyValueList = ({ items }) => (
  <Stack spacing={1}>
    {items.map(({ label, value }) => (
      <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
          {value ?? '-'}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export const HealthPanel = () => {
  const { data, isLoading, isFetching, refetch } = useNotificationHealth({
    target_app_module: 'habitat_99',
  });
  const warnings = data?.warnings || [];
  const schedulerJobs = data?.scheduler?.jobs || [];

  return (
    <Stack spacing={2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          {statusChip(data?.status || 'running')}
          <Typography variant="body2" color="text.secondary">
            Última lectura: {formatDateTime(data?.generated_at)}
          </Typography>
        </Stack>
        <CustomButton
          variant="outlined"
          buttonIcon={Refresh}
          onClick={() => refetch()}
          isLoading={isLoading || isFetching}
          size="small"
        >
          Actualizar
        </CustomButton>
      </Stack>
      {isLoading && <LoadingSkeleton />}
      {!isLoading && data && (
        <>
          <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
            <Grid item xs={12} md={3}>
              <Metric
                label="Reglas activas"
                value={data.rules?.active}
                icon={Rule}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Metric
                label="Ejecuciones fallidas 24h"
                value={data.jobs_last_24h?.by_status?.failed || 0}
                icon={InfoOutlined}
                color="#d32f2f"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Metric
                label="Tokens activos"
                value={data.device_tokens?.active}
                icon={NotificationsActive}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Metric
                label="Push enviados 24h"
                value={data.push_last_24h?.delivery_attempts_by_status?.sent || 0}
                icon={CheckCircle}
                color="#00897b"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Entorno
                </Typography>
                <KeyValueList
                  items={[
                    {
                      label: 'Producción',
                      value: data.environment?.is_production ? 'Sí' : 'No',
                    },
                    {
                      label: 'Flag push productivo',
                      value: data.environment?.production_push_enabled ? 'Activo' : 'Inactivo',
                    },
                    {
                      label: 'Push real permitido',
                      value: data.environment?.production_push_allowed ? 'Sí' : 'No',
                    },
                    {
                      label: 'Acceso por módulo',
                      value: data.security?.module_access_enforced ? 'Aplicado' : 'Revisar',
                    },
                  ]}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Ejecuciones automáticas
                </Typography>
                <KeyValueList
                  items={[
                    {
                      label: 'Inicializado',
                      value: data.scheduler?.initialized ? 'Sí' : 'No',
                    },
                    {
                      label: 'Ejecutando',
                      value: data.scheduler?.running ? 'Sí' : 'No',
                    },
                    {
                      label: 'Ejecuciones configuradas',
                      value: schedulerJobs.length,
                    },
                  ]}
                />
                <Stack spacing={0.5} sx={{ pt: 2 }}>
                  {schedulerJobs.map((job) => (
                    <Typography key={job.id} variant="body2" color="text.secondary">
                      {labelForJob(job)}: {formatDateTime(job.next_run_time)}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Eventos últimos 30 días
                </Typography>
                <KeyValueList
                  items={Object.entries(data.events_last_30d?.by_type || {}).map(([label, value]) => ({
                    label: labelForEventType(label),
                    value,
                  }))}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Alertas
                </Typography>
                {warnings.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Sin alertas operativas.
                  </Typography>
                )}
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {warnings.map((warning) => (
                    <Chip
                      key={warning}
                      label={labelForWarning(warning)}
                      size="small"
                      color="warning"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};
